import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { itemDisplayName } from "@/lib/inventory";
import {
  useCatalogueCategories,
  useCatalogueItems,
  type CatalogueCategory,
  type CatalogueItem,
} from "@/hooks/use-labelling";

/**
 * The relabel drawer: category → sub-category → item, catalogue only, no free text.
 *
 * Deliberately not `components/waste/relabel-picker.tsx`, though it follows the
 * same drill. That component's three data hooks call `/food-categories`,
 * `/sites/{id}/inventory` and `/inventory-lists/{id}/items`, all behind
 * `require_permission("inventory:read")` — which rejects a caller with no org
 * context *before* checking the permission, and a labeller has no org by design.
 * The `/labelling/catalogue/*` endpoints serve the same shapes; the interaction is
 * reused, the fetching cannot be.
 *
 * Committing at any of the three levels is allowed, exactly as the staff picker
 * allows: the depth of the choice is the tier the event ends up at.
 */
export type RelabelChoice = {
  category_id?: string | null;
  subcategory_id?: string | null;
  inventory_item_id?: string | null;
};

/** Below this, the box narrows the level on screen; at it, it searches the tree. */
const MIN_SEARCH = 2;
const SEARCH_DEBOUNCE_MS = 200;
/** Stable empty default, so the memos below do not recompute on every render. */
const NO_CATEGORIES: CatalogueCategory[] = [];

function useDebounced(value: string, ms: number) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export function RelabelDrawer({
  eventId,
  isSubmitting,
  onClose,
  onChoose,
}: {
  eventId: string | null;
  isSubmitting: boolean;
  onClose: () => void;
  onChoose: (choice: RelabelChoice) => void;
}) {
  const open = eventId !== null;
  const [category, setCategory] = useState<CatalogueCategory | null>(null);
  const [subcategory, setSubcategory] = useState<CatalogueCategory | null>(null);
  const [search, setSearch] = useState("");

  // A different item resets the drill. Keyed by id, so a refetch that swaps object
  // identity does not throw away a drill in progress. Focus moves to the search
  // box on open — via a ref rather than `autoFocus`, which eslint's a11y rule
  // rejects (and rightly: it fires on mount regardless of why the mount happened).
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setCategory(null);
    setSubcategory(null);
    setSearch("");
    if (eventId) searchRef.current?.focus();
  }, [eventId]);

  // Two characters in, the box stops narrowing the level on screen and starts
  // searching the catalogue instead — the whole tree at once, so an item is
  // reachable without knowing which sub-category holds it. Clearing it drops back
  // into the drill, still where it was left.
  const typed = search.trim();
  const searching = typed.length >= MIN_SEARCH;
  // The request lags the keystrokes by a beat, so typing a word is one fetch and
  // not one per letter. While the two disagree the list is a skeleton rather than a
  // stale answer to the previous term.
  const settled = useDebounced(typed, SEARCH_DEBOUNCE_MS);
  const catchingUp = searching && settled !== typed;

  const categories = useCatalogueCategories(open);
  const items = useCatalogueItems(
    eventId,
    searching ? null : (subcategory?.id ?? null),
    searching ? settled : "",
  );

  const all = categories.data ?? NO_CATEGORIES;
  const topLevel = useMemo(() => all.filter((c) => c.parent_id === null), [all]);
  const children = useMemo(
    () => all.filter((c) => c.parent_id === category?.id),
    [all, category?.id],
  );

  const query = typed.toLowerCase();
  const hits = useMemo(() => {
    if (!searching) return null;
    const named = (name: string) => name.toLowerCase().includes(query);
    const byId = new Map(all.map((c) => [c.id, c]));
    return {
      // Most specific first: a labeller searching a food name wants the food, not
      // the aisle it sits in.
      items: (items.data ?? []).map((item) => ({
        item,
        path: byId.get(item.subcategory_id ?? "")?.name ?? null,
      })),
      subcategories: all
        .filter((c) => c.parent_id !== null && named(c.name))
        .map((c) => ({ subcategory: c, parent: byId.get(c.parent_id!) ?? null })),
      categories: topLevel.filter((c) => named(c.name)),
    };
  }, [searching, query, all, topLevel, items.data]);

  if (!open) return null;

  const level = subcategory ? "item" : category ? "subcategory" : "category";

  function matches(name: string) {
    return !query || name.toLowerCase().includes(query);
  }

  return (
    <div className="mx-auto max-w-[1120px] px-6 pb-16">
      <div className="rounded-lg border border-black/[0.06] bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!searching && (category || subcategory) && (
              <button
                type="button"
                onClick={() => (subcategory ? setSubcategory(null) : setCategory(null))}
                className="inline-flex items-center gap-1.5 rounded-md p-1 text-xs font-medium text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700"
              >
                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                Back
              </button>
            )}
            <span className="text-sm font-semibold text-gray-900">
              {searching
                ? `Searching the catalogue for “${typed}”`
                : level === "category"
                  ? "Choose the correct label"
                  : level === "subcategory"
                    ? category!.name
                    : `${category!.name} · ${subcategory!.name}`}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-medium text-gray-500 underline underline-offset-2 transition-colors duration-150 hover:text-gray-700"
          >
            Close (Esc)
          </button>
        </div>

        <div className="relative mb-3.5">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-300"
            strokeWidth={1.5}
          />
          <Input
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search catalogue…"
            className="pl-9"
          />
        </div>

        {searching ? (
          catchingUp || items.isLoading ? (
            <ChipSkeleton />
          ) : (
            <SearchHits hits={hits!} isSubmitting={isSubmitting} term={typed} onChoose={onChoose} />
          )
        ) : level === "item" && items.isLoading ? (
          <ChipSkeleton />
        ) : level !== "item" && categories.isLoading ? (
          <ChipSkeleton />
        ) : (
          <div className="flex flex-wrap gap-2">
            {level === "category" &&
              topLevel
                .filter((c) => matches(c.name))
                .map((c) => (
                  <Chip key={c.id} disabled={isSubmitting} onClick={() => setCategory(c)}>
                    {c.name}
                  </Chip>
                ))}

            {level === "subcategory" && (
              <>
                <Chip
                  disabled={isSubmitting}
                  onClick={() => onChoose({ category_id: category!.id })}
                  emphasis
                >
                  Use “{category!.name}” · L2
                </Chip>
                {children
                  .filter((c) => matches(c.name))
                  .map((c) => (
                    <Chip key={c.id} disabled={isSubmitting} onClick={() => setSubcategory(c)}>
                      {c.name}
                    </Chip>
                  ))}
              </>
            )}

            {level === "item" && (
              <>
                <Chip
                  disabled={isSubmitting}
                  onClick={() =>
                    onChoose({ category_id: category!.id, subcategory_id: subcategory!.id })
                  }
                  emphasis
                >
                  Use “{subcategory!.name}” · L3
                </Chip>
                {(items.data ?? [])
                  .filter((item) => matches(itemDisplayName(item)))
                  .map((item) => (
                    <Chip
                      key={item.id}
                      disabled={isSubmitting}
                      onClick={() =>
                        onChoose({
                          category_id: category!.id,
                          subcategory_id: subcategory!.id,
                          inventory_item_id: item.id,
                        })
                      }
                    >
                      {itemDisplayName(item)}
                    </Chip>
                  ))}
              </>
            )}
          </div>
        )}

        {!searching && level === "item" && !items.isLoading && (items.data ?? []).length === 0 && (
          <p className="text-xs text-gray-400">
            No catalogue items under this sub-category. Commit at L3 above, or go back.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * One flat list of everything the term matched, at every tier, each committable in
 * one tap. Items lead: a labeller who typed a food name wants the food, not the
 * aisle it sits in.
 *
 * Every chip states the tier it commits to, because a search result has no drill
 * position to imply it — "Fresh Fruit" and "Bananas" sit side by side, and picking
 * the wrong one silently records the wrong depth.
 */
function SearchHits({
  hits,
  isSubmitting,
  term,
  onChoose,
}: {
  hits: {
    items: { item: CatalogueItem; path: string | null }[];
    subcategories: { subcategory: CatalogueCategory; parent: CatalogueCategory | null }[];
    categories: CatalogueCategory[];
  };
  isSubmitting: boolean;
  term: string;
  onChoose: (choice: RelabelChoice) => void;
}) {
  const total = hits.items.length + hits.subcategories.length + hits.categories.length;
  if (total === 0) {
    return (
      <p className="text-xs text-gray-400">
        Nothing in the catalogue matches “{term}”. Clear the search to browse by category instead.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {hits.items.map(({ item, path }) => (
        <Chip
          key={item.id}
          disabled={isSubmitting}
          onClick={() =>
            onChoose({
              category_id: item.category_id,
              subcategory_id: item.subcategory_id,
              inventory_item_id: item.id,
            })
          }
        >
          {itemDisplayName(item)}
          {path && <Context>{path}</Context>}
          <Tier>L4</Tier>
        </Chip>
      ))}

      {hits.subcategories.map(({ subcategory, parent }) => (
        <Chip
          key={subcategory.id}
          disabled={isSubmitting}
          onClick={() =>
            onChoose({ category_id: parent?.id ?? null, subcategory_id: subcategory.id })
          }
        >
          {subcategory.name}
          {parent && <Context>{parent.name}</Context>}
          <Tier>L3</Tier>
        </Chip>
      ))}

      {hits.categories.map((c) => (
        <Chip key={c.id} disabled={isSubmitting} onClick={() => onChoose({ category_id: c.id })}>
          {c.name}
          <Tier>L2</Tier>
        </Chip>
      ))}
    </div>
  );
}

function Context({ children }: { children: React.ReactNode }) {
  return <span className="ml-1.5 font-normal text-gray-400">· {children}</span>;
}

function Tier({ children }: { children: React.ReactNode }) {
  return <span className="ml-1.5 text-[10px] font-semibold text-gray-400">{children}</span>;
}

function Chip({
  children,
  onClick,
  disabled,
  emphasis,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  emphasis?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 " +
        (emphasis
          ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
          : "border-gray-200 bg-white text-gray-700 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700")
      }
    >
      {children}
    </button>
  );
}

function ChipSkeleton() {
  return (
    <div className="flex flex-wrap gap-2">
      {[64, 92, 76, 108, 84].map((width, index) => (
        <Skeleton key={index} className="h-7 rounded-full" style={{ width }} />
      ))}
    </div>
  );
}
